import { Stack } from '@mantine/core';
import type { TFunction } from 'i18next';
import type { Outcome } from '../../../../api/strategy-client/strategy-client.type';
import { downloadCSV } from '../../../../helpers/csv-download';
import { formatDate } from '../../../../helpers/date-time-helpers';
import { primaryBase, skyLight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { TableColumn } from '../../../data-management/components/table';
import { RiskBadge } from '../risk-analysis';
import { getRiskCategory } from '../risk-analysis.helpers';
import { TruncatedText } from '../truncated-text';

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function CompletionBar({ percentage }: { percentage: number }) {
  return (
    <Stack gap={1}>
      <Text size="small" weight="medium">
        {percentage}%
      </Text>
      <div
        style={{
          width: '100%',
          height: '4px',
          backgroundColor: skyLight,
          borderRadius: '2px',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: primaryBase,
            borderRadius: '2px',
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </Stack>
  );
}

/**
 * Returns the columns for the outcomes table.
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @returns {TableColumn<Outcome>[]} - The columns for the outcomes table.
 */
const getOutcomeTableColumns = (t: TFunction): TableColumn<Outcome>[] => {
  return [
    {
      key: 'name',
      label: `${t('targets.outcome_name')}`,
      width: '20%',
      sortable: true,
      render: (_, outcome) => <Text weight="medium">{outcome.name}</Text>,
    },
    {
      key: 'description',
      label: 'Description',
      width: '20%',
      render: (_, outcome) => <TruncatedText text={outcome.description} onToggle={() => {}} />,
    },
    {
      key: 'percent_progress',
      label: 'Progress',
      width: '12%',
      sortable: true,
      render: (_, outcome) => <CompletionBar percentage={outcome.percent_progress} />,
    },
    {
      key: 'start_date',
      label: 'Start Date',
      width: '10%',
      sortable: true,
      render: (_, outcome) =>
        outcome.start_date ? formatDate(outcome.start_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
    },
    {
      key: 'end_date',
      label: 'End Date',
      width: '10%',
      sortable: true,
      render: (_, outcome) =>
        outcome.end_date ? formatDate(outcome.end_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
    },
    {
      key: 'flora_annotations.risk_category' as keyof Outcome,
      label: 'Risk Category',
      width: '13%',
      sortable: true,
      render: (_, outcome) => {
        const riskCategory = getRiskCategory(outcome.flora_annotations?.risk_score);
        return <RiskBadge color={riskCategory.color}>{riskCategory.label}</RiskBadge>;
      },
    },
    {
      key: 'flora_annotations.risk_score' as keyof Outcome,
      label: 'Risk Score',
      width: '10%',
      sortable: true,
      render: (_, outcome) => outcome.flora_annotations?.risk_score?.toFixed(0) ?? 'N/A',
    },
  ];
};

/**
 * Downloads a CSV file of the outcomes table.
 * @param {Outcome[]} outcomes - The outcomes to download.
 * @param {TFunction} t - Translation function from `useTranslation()`
 *
 * Returns {void}
 * Throws {Error} if the CSV file cannot be downloaded.
 */
const handleDownloadOutcomeTableCSV = (outcomes: Outcome[], t: TFunction) => {
  try {
    const headers = [
      `${t('common.outcome')} Name`,
      'Description',
      'Progress %',
      'Start Date',
      'End Date',
      'Risk Category',
      'Risk Score',
    ];

    const rows = outcomes.map((outcome) => [
      outcome.name,
      outcome.description,
      outcome.percent_progress,
      outcome.start_date ? formatDate(outcome.start_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
      outcome.end_date ? formatDate(outcome.end_date, { timezone, format: 'MM/DD/YYYY' }) : 'No date',
      getRiskCategory(outcome.flora_annotations?.risk_score).label,
      outcome.flora_annotations?.risk_score?.toFixed(0) ?? 'N/A',
    ]);

    downloadCSV([headers, ...rows], `${t('common.outcomes').toLowerCase()}.csv`);
  } catch (error) {
    console.error('Error downloading CSV:', error);
  }
};

export { getOutcomeTableColumns, handleDownloadOutcomeTableCSV };
